
import React, { useEffect, useState } from "react";
import BreadcrumbConstructor from "components/Breadcrumb";
import { useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { getUserPlantsDataById } from "utils/Requests/Plant";
import _ from "lodash";
import { NotFoundPlantAlert } from "components/Alerts";
import { useTranslation } from "react-i18next";
import { PlantSummary } from "components/Summary/PlantSummary";
import { CardPlotSummary } from "components/Cards/CardPlotSummary";
import { CardPlantProductionTables } from "components/Cards/CardPlantProductionTables";

import {
    getIconByGlobalPlantStatus,
    isSolarPhotovoltaicByCaller,
    isSolarThermalByCaller,
    isThermalByCaller,
    isWaterByCaller
} from "../../utils/Types";

import config from "/app/src/config.js";

const isMobileOnly = config.mbl_version;


/*
 * ============================================================
 * SEYE VERSION
 * ============================================================
 */

const getSeyeVersion = (plant) => {

    const serialNumber = Number(plant.set_serial_number);

    if (!serialNumber) {
        return "";
    }

    if (serialNumber <= 2503110000) {
        return "2.0";
    }

    return "";
};


/*
 * ============================================================
 * MOBILE HEADER
 * ============================================================
 */

const PlantMobileHeader = ({ plant, caller }) => {

    const style = {
        fontSize: "15px",
        padding: "10px 20px",
        backgroundColor: "rgba(237, 237, 237, 0.68)",
        borderBottom: "1px solid rgb(195, 194, 194)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontWeight: "700"
    };

    const icon = getIconByGlobalPlantStatus(
        { caller },
        {
            plant_status_id: plant.plant_status_id,
            plant_status_name: plant.plant_status_name,
            plant_status_since: plant.plant_status_since,
            plant_manual_status: plant.plant_manual_status,
            plant_manual_status_since: plant.plant_manual_status_since
        }
    );

    const seyeVersion = getSeyeVersion(plant);

    const hasKappa =
        isSolarPhotovoltaicByCaller({ caller }) ||
        isSolarThermalByCaller({ caller }) ||
        isWaterByCaller({ caller });

    const kappa = Number(
        _.get(plant, "last_30_plant_performance", 0)
    );

    return (
        <div style={style}>

            <div>
                <div>{plant.plant_name}</div>

                <div style={{ fontSize: "12px" }}>
                    {plant.plant_town_name}
                </div>
            </div>

            <div className="d-flex align-items-center">

                {hasKappa && (
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            marginRight: "10px"
                        }}
                    >
                        <span
                            style={{
                                display: "inline-block",
                                borderTop: "1px solid currentColor",
                                lineHeight: "0.9",
                                paddingTop: "1px"
                            }}
                        >
                            {"\u03F0"}
                        </span>
                        :&nbsp;
                        {Number.isFinite(kappa)
                            ? kappa.toFixed(0)
                            : 0}%
                    </span>
                )}

                {seyeVersion && (
                    <span style={{ marginRight: "8px" }}>
                        {seyeVersion}
                    </span>
                )}

                {icon}

            </div>
        </div>
    );
};


/*
 * ============================================================
 * NAVIGATION
 * ============================================================
 */

const tabs = [
    {
        id: "statistics",
        label: "Estadístiques"
    },
    {
        id: "data",
        label: "Dades"
    },
    {
        id: "alerts",
        label: "Alertes"
    }
];



/*
 * ============================================================
 * ICONOS ORIGINALES DE RDMES RESIDENCIAL
 * ============================================================
 *
 * Estadístiques -> SVGRepo.stats
 * Dades         -> SVGRepo.count
 * Alertes       -> SVGRepo.warning
 *
 * Iconos reutilizados sin dependencias adicionales.
 */

const TabIcon = ({ id, active }) => {

    const color = active ? "#2563eb" : "#6b7280";

    const icons = {
        statistics: (
            <path d="M20,2H4A2,2,0,0,0,2,4V20a2,2,0,0,0,2,2H20a2,2,0,0,0,2-2V4A2,2,0,0,0,20,2ZM9,17a1,1,0,0,1-2,0V15a1,1,0,0,1,2,0Zm4,0a1,1,0,0,1-2,0V11a1,1,0,0,1,2,0Zm4,0a1,1,0,0,1-2,0V7a1,1,0,0,1,2,0Z" />
        ),

        data: (
            <path d="M12,3A10,10,0,0,0,2,13a9.89,9.89,0,0,0,3.74,7.78,1,1,0,0,0,.62.22H17.64a1,1,0,0,0,.62-.22A9.89,9.89,0,0,0,22,13,10,10,0,0,0,12,3Zm4.83,6.55-3,4.56A1.91,1.91,0,0,1,14,15a2,2,0,1,1-2-2h.13l3-4.56a1,1,0,0,1,1.66,1.1Z" />
        ),

        alerts: (
            <path d="M22.25,17.55,14.63,3.71a3,3,0,0,0-5.26,0L1.75,17.55A3,3,0,0,0,4.38,22H19.62a3,3,0,0,0,2.63-4.45ZM12,18a1,1,0,1,1,1-1A1,1,0,0,1,12,18Zm1-5a1,1,0,0,1-2,0V9a1,1,0,0,1,2,0Z" />
        )
    };

    return (
        <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{
                color: color,
                flexShrink: 0
            }}
            aria-hidden="true"
        >
            {icons[id] || null}
        </svg>
    );
};


/*
 * ============================================================
 * TAB NAVIGATION
 * ============================================================
 */

const PlantTabs = ({ activeTab, onChange, mobile }) => {

    return (
        <nav
            aria-label="Seccions de la instal·lació"
            style={{
                display: "flex",
                justifyContent: "space-around",
                alignItems: "stretch",
                backgroundColor: "#ffffff",
                borderTop: mobile
                    ? "1px solid #e5e5e5"
                    : "none",
                borderBottom: mobile
                    ? "none"
                    : "1px solid #e5e5e5",
                position: mobile ? "fixed" : "relative",
                bottom: mobile ? 0 : "auto",
                left: mobile ? 0 : "auto",
                right: mobile ? 0 : "auto",
                width: "100%",
                zIndex: mobile ? 1050 : "auto",
                paddingBottom: mobile
                    ? "env(safe-area-inset-bottom, 0px)"
                    : 0
            }}
        >
            {tabs.map((tab) => {

                const active = activeTab === tab.id;

                return (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => onChange(tab.id)}
                        aria-current={active ? "page" : undefined}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            border: "none",
                            background: "transparent",
                            padding: mobile
                                ? "10px 4px"
                                : "14px 12px",
                            display: "flex",
                            flexDirection: mobile
                                ? "column"
                                : "row",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "5px",
                            cursor: "pointer",
                            color: active
                                ? "#3267dc"
                                : "#737b8d",
                            fontSize: mobile
                                ? "11px"
                                : "14px",
                            fontWeight: active ? 600 : 400,
                            borderBottom: !mobile && active
                                ? "2px solid #3267dc"
                                : "2px solid transparent"
                        }}
                    >
                        <TabIcon
                            id={tab.id}
                            active={active}
                        />

                        <span>{tab.label}</span>
                    </button>
                );
            })}
        </nav>
    );
};


/*
 * ============================================================
 * PAGE PLANT
 * ============================================================
 */

export default function PagePlant({ ...props }) {

    const params = useParams();
    const { t } = useTranslation();

    /*
     * Pestaña seleccionada
     */
    const [activeTab, setActiveTab] = useState("statistics");

    /*
     * Configuración de la instalación
     */
    const [configuration, setConfiguration] = useState({
        ...props,
        elId: params.id,
        plant: {},
        isLoading: true,
        isError: false
    });

    /*
     * ========================================================
     * LOAD PLANT
     * ========================================================
     */

    useEffect(() => {

        let isMounted = true;

        setConfiguration((prev) => ({
            ...prev,
            ...props,
            elId: params.id,
            plant: {},
            isLoading: true,
            isError: false
        }));

        setActiveTab("statistics");

        getUserPlantsDataById(
            params.id,

            (response) => {
                if (!isMounted) {
                    return;
                }

                setConfiguration((prev) => ({
                    ...prev,
                    plant: response.data,
                    isLoading: false,
                    isError: false
                }));
            },

            () => {
                if (!isMounted) {
                    return;
                }

                setConfiguration((prev) => ({
                    ...prev,
                    plant: {},
                    isLoading: false,
                    isError: true
                }));
            }
        );

        return () => {
            isMounted = false;
        };

    }, [params.id]);


    /*
     * ========================================================
     * CONTENT
     * ========================================================
     */

    const isReady =
        !configuration.isLoading &&
        !configuration.isError &&
        !_.isEmpty(configuration.plant);

    const contentColumnClass = isMobileOnly
        ? "col-12 col-mbl"
        : "col-12";

    /*
     * ========================================================
     * RENDER
     * ========================================================
     */

    return (
        <>

            {/*
             * =================================================
             * HEADER
             * =================================================
             */}

            <div
                className={`content-header row ${
                    isMobileOnly ? "row-mbl" : ""
                }`}
            >

                {configuration.isLoading &&
                    !configuration.isError && (
                        <Skeleton className="mt-1" />
                    )
                }

                {configuration.isError && (
                    <span className="mt-1 ms-1">
                        Error...
                    </span>
                )}

                {isReady && (
                    <>
                        {isMobileOnly ? (
                            <PlantMobileHeader
                                plant={configuration.plant}
                                caller={configuration.caller}
                            />
                        ) : (
                            <BreadcrumbConstructor
                                crumbs={[
                                    { id: "dashboard" },
                                    { id: "plants" },
                                    { id: configuration.caller },
                                    {
                                        id: "plant",
                                        text:
                                            configuration.plant.plant_name ??
                                            t("plant.not_found_breadcrumb"),
                                        disabled: true
                                    }
                                ]}
                            />
                        )}
                    </>
                )}

            </div>


            {/*
             * =================================================
             * CONTENT BODY
             * =================================================
             */}

            <div
                className="content-body mb-1"
                style={{
                    paddingBottom: isMobileOnly
                        ? "90px"
                        : undefined
                }}
            >

                {_.isEmpty(configuration.plant) &&
                    !configuration.isLoading &&
                    !configuration.isError && (
                        <NotFoundPlantAlert />
                    )
                }

                {isReady && (
                    <section
                        id={`dashboard-${configuration.caller}-${configuration.elId}`}
                    >

                        {/*
                         * Navegación superior para escritorio
                         */}

                        {!isMobileOnly && (
                            <PlantTabs
                                activeTab={activeTab}
                                onChange={setActiveTab}
                                mobile={false}
                            />
                        )}


                        {/*
                         * =====================================
                         * ESTADÍSTIQUES
                         * =====================================
                         */}

                        {activeTab === "statistics" && (

                            <div
                                className={`row match-height ${
                                    isMobileOnly
                                        ? "row-mbl match-height-mbl"
                                        : ""
                                }`}
                            >

                                <div className={contentColumnClass}>

                                    <CardPlotSummary
                                        caller={configuration.caller}
                                        plantId={configuration.elId}
                                    />

                                </div>

                            </div>

                        )}


                        {/*
                         * =====================================
                         * DADES / COMPTADOR
                         * =====================================
                         */}

                        {activeTab === "data" && (

                            <div
                                className={`row match-height ${
                                    isMobileOnly
                                        ? "row-mbl match-height-mbl"
                                        : ""
                                }`}
                            >

                                <div className={contentColumnClass}>

                                    <CardPlantProductionTables
                                        caller={configuration.caller}
                                        plantId={configuration.elId}
                                        tableProps={{
                                            fixedHeader: true,

                                            fixedHeaderScrollHeight:
                                                isThermalByCaller(
                                                    configuration
                                                )
                                                    ? 296
                                                    : 550,

                                            defaultSortAsc: false
                                        }}
                                    />

                                </div>

                            </div>

                        )}


                        {/*
                         * =====================================
                         * ALERTES
                         * =====================================
                         */}

                        {activeTab === "alerts" && (

                            <div
                                className={`row match-height ${
                                    isMobileOnly
                                        ? "row-mbl match-height-mbl"
                                        : ""
                                }`}
                            >

                                <div className={contentColumnClass}>

                                    <PlantSummary
                                        {...configuration}
                                        data={configuration.plant}
                                        caller={configuration.caller}
                                        type={1}
                                    />

                                </div>

                            </div>

                        )}


                        {/*
                         * =====================================
                         * DESCRIPCIÓ - COMENTADA TEMPORALMENTE
                         * =====================================
                         *
                         * El componente se conserva en el código,
                         * pero React no lo renderiza ni lo monta.
                         *
                         * Para recuperarlo, descomentar este bloque.
                         *
                         * <div
                         *     className={`row match-height ${
                         *         isMobileOnly
                         *             ? "row-mbl match-height-mbl"
                         *             : ""
                         *     }`}
                         * >
                         *
                         *     <div className={contentColumnClass}>
                         *
                         *         <PlantSummary
                         *             {...configuration}
                         *             data={configuration.plant}
                         *             type={2}
                         *         />
                         *
                         *     </div>
                         *
                         * </div>
                         *
                         */}

                    </section>
                )}

            </div>


            {/*
             * =================================================
             * MOBILE BOTTOM NAVIGATION
             * =================================================
             */}

            {isMobileOnly && isReady && (

                <PlantTabs
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    mobile={true}
                />

            )}

        </>
    );
}
